import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GripVertical,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Lock,
  User,
  Calendar,
  ChevronDown,
  Plus,
  Filter,
  Search,
  MoreHorizontal,
  Paperclip,
  X,
  Loader2,
} from "lucide-react";
import { taskPipelineApi, tasksApi, usersApi } from "../../services/api";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { motion, AnimatePresence } from "framer-motion";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  matterId: string;
  reCase: any;
}

interface PipelineTask {
  _id: string;
  title: string;
  description?: string;
  pipelineStage: string;
  status: string;
  priority: "low" | "normal" | "high" | "urgent";
  dueAt: string | null;
  ownerUserId: { _id: string; name: string; email: string } | null;
  assigneeUserIds: { _id: string; name: string }[];
  requiresEvidence?: boolean;
  dependencies?: string[];
  completedAt: string | null;
  order: number;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PIPELINE_STAGES = [
  { key: "intake", label: "Intake", color: "bg-slate-100 text-slate-700" },
  { key: "due_diligence", label: "Due Diligence", color: "bg-blue-100 text-blue-700" },
  { key: "requisitions", label: "Requisitions", color: "bg-indigo-100 text-indigo-700" },
  { key: "signing", label: "Signing", color: "bg-purple-100 text-purple-700" },
  { key: "pre_closing", label: "Pre-Closing", color: "bg-amber-100 text-amber-700" },
  { key: "closing_day", label: "Closing Day", color: "bg-cyan-100 text-cyan-700" },
  { key: "post_closing", label: "Post-Closing", color: "bg-emerald-100 text-emerald-700" },
  { key: "reporting", label: "Reporting", color: "bg-slate-100 text-slate-600" },
];

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  normal: "bg-slate-100 text-slate-600",
  low: "bg-slate-50 text-slate-400",
};

const PRIORITY_OPTIONS = ["urgent", "high", "normal", "low"];

const formatLabel = (s: string) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// ─── Skeleton Card ────────────────────────────────────────────────────────────

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-3 animate-pulse">
    <div className="h-4 bg-slate-200 rounded w-3/4 mb-3" />
    <div className="h-3 bg-slate-100 rounded w-1/2 mb-2" />
    <div className="flex gap-2">
      <div className="h-5 w-5 rounded-full bg-slate-200" />
      <div className="h-3 bg-slate-100 rounded w-16 mt-1" />
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const RealEstatePipelineTab: React.FC<Props> = ({ matterId, reCase }) => {
  const [columns, setColumns] = useState<Record<string, PipelineTask[]>>({});
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPriority, setFilterPriority] = useState<string>("");
  const [filterAssignee, setFilterAssignee] = useState<string>("");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [closingWarning, setClosingWarning] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuLoading, setMenuLoading] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // ─── Data Fetching ──────────────────────────────────────────────────────────

  const fetchTasks = useCallback(async () => {
    try {
      const res = await taskPipelineApi.getByMatter(matterId);
      const data = res.data || {};
      // Ensure every stage has an array
      const normalized: Record<string, PipelineTask[]> = {};
      PIPELINE_STAGES.forEach((stage) => {
        normalized[stage.key] = data[stage.key] || [];
      });
      setColumns(normalized);
    } catch (err) {
      console.error("Failed to fetch pipeline tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersApi.list();
      setUsers(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [fetchTasks, fetchUsers]);

  // ─── Close outside menu ─────────────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ─── Close-Ready Gate ───────────────────────────────────────────────────────

  const checkPreClosingComplete = useCallback(
    (cols: Record<string, PipelineTask[]>): boolean => {
      const preClosingTasks = cols["pre_closing"] || [];
      if (preClosingTasks.length === 0) return true;
      return preClosingTasks.every(
        (t) => t.status === "done" || t.completedAt != null
      );
    },
    []
  );

  // ─── Drag and Drop ─────────────────────────────────────────────────────────

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;
    const taskId = draggableId;

    // Close-Ready Gate: warn if moving to closing_day with incomplete pre_closing
    if (destStage === "closing_day" && sourceStage !== "closing_day") {
      if (!checkPreClosingComplete(columns)) {
        setClosingWarning(
          "Warning: Some pre-closing tasks are still incomplete"
        );
        setTimeout(() => setClosingWarning(null), 6000);
      }
    }

    // Optimistic update
    const newColumns = { ...columns };
    const sourceItems = [...(newColumns[sourceStage] || [])];
    const [moved] = sourceItems.splice(source.index, 1);
    if (!moved) return;

    newColumns[sourceStage] = sourceItems;

    const destItems =
      sourceStage === destStage
        ? newColumns[sourceStage]
        : [...(newColumns[destStage] || [])];
    moved.pipelineStage = destStage;
    destItems.splice(destination.index, 0, moved);

    if (sourceStage !== destStage) {
      newColumns[destStage] = destItems;
    }

    setColumns(newColumns);

    // Persist to API
    try {
      await taskPipelineApi.updateStage(taskId, { stage: destStage });
    } catch (err) {
      console.error("Failed to update pipeline stage:", err);
      fetchTasks(); // rollback
    }
  };

  // ─── Quick Actions ──────────────────────────────────────────────────────────

  const handleMarkComplete = async (taskId: string) => {
    setMenuLoading(taskId);
    try {
      await tasksApi.changeStatus(taskId, "done");
      setActiveMenu(null);
      fetchTasks();
    } catch (err) {
      console.error("Failed to mark complete:", err);
    } finally {
      setMenuLoading(null);
    }
  };

  const handleMarkBlocked = async (taskId: string) => {
    setMenuLoading(taskId);
    try {
      await tasksApi.changeStatus(taskId, "blocked");
      setActiveMenu(null);
      fetchTasks();
    } catch (err) {
      console.error("Failed to mark blocked:", err);
    } finally {
      setMenuLoading(null);
    }
  };

  // ─── Filtering ──────────────────────────────────────────────────────────────

  const filterTasks = useCallback(
    (tasks: PipelineTask[]): PipelineTask[] => {
      let filtered = tasks;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter((t) =>
          t.title.toLowerCase().includes(q)
        );
      }

      if (filterPriority) {
        filtered = filtered.filter((t) => t.priority === filterPriority);
      }

      if (filterAssignee) {
        filtered = filtered.filter(
          (t) =>
            t.ownerUserId?._id === filterAssignee ||
            t.assigneeUserIds?.some((a) => a._id === filterAssignee)
        );
      }

      if (hideCompleted) {
        filtered = filtered.filter(
          (t) => t.status !== "done" && t.completedAt == null
        );
      }

      return filtered;
    },
    [searchQuery, filterPriority, filterAssignee, hideCompleted]
  );

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getInitials = (name: string): string =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("");

  const isOverdue = (dueAt: string | null): boolean => {
    if (!dueAt) return false;
    return new Date(dueAt) < new Date();
  };

  const hasActiveFilters =
    !!searchQuery || !!filterPriority || !!filterAssignee || hideCompleted;

  const clearFilters = () => {
    setSearchQuery("");
    setFilterPriority("");
    setFilterAssignee("");
    setHideCompleted(false);
  };

  // ─── Loading State ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "70vh" }}>
        {PIPELINE_STAGES.map((stage) => (
          <div key={stage.key} className="flex-shrink-0 w-72">
            <div className="flex items-center gap-2 mb-3 px-1">
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-bold ${stage.color}`}
              >
                {stage.label}
              </span>
            </div>
            <div className="bg-slate-50 rounded-xl p-2 space-y-2 min-h-[200px]">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 bg-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
            hasActiveFilters
              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Filter className="h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
              {[filterPriority, filterAssignee, hideCompleted]
                .filter(Boolean)
                .length}
            </span>
          )}
        </button>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
          >
            Clear all
          </button>
        )}
      </div>

      {/* ── Expanded Filters ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
              {/* Priority Filter */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500">
                  Priority
                </label>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-emerald-600"
                >
                  <option value="">All</option>
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {formatLabel(p)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Assignee Filter */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500">
                  Assignee
                </label>
                <select
                  value={filterAssignee}
                  onChange={(e) => setFilterAssignee(e.target.value)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-emerald-600"
                >
                  <option value="">All</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hide Completed Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideCompleted}
                  onChange={(e) => setHideCompleted(e.target.checked)}
                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs text-slate-600">Hide completed</span>
              </label>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Closing Warning Banner ──────────────────────────────────────────── */}
      <AnimatePresence>
        {closingWarning && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-center gap-2 mb-4"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>{closingWarning}</span>
            <button
              onClick={() => setClosingWarning(null)}
              className="ml-auto text-amber-500 hover:text-amber-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Kanban Board ────────────────────────────────────────────────────── */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div
          className="flex gap-3 overflow-x-auto pb-4"
          style={{ minHeight: "70vh" }}
        >
          {PIPELINE_STAGES.map((stage) => {
            const rawTasks = columns[stage.key] || [];
            const tasks = filterTasks(rawTasks);

            return (
              <div key={stage.key} className="flex-shrink-0 w-72">
                {/* ── Column Header ──────────────────────────────────────── */}
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-bold ${stage.color}`}
                    >
                      {stage.label}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">
                      {tasks.length}
                    </span>
                  </div>
                </div>

                {/* ── Droppable Column ───────────────────────────────────── */}
                <Droppable droppableId={stage.key}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`bg-slate-50 rounded-xl p-2 space-y-2 min-h-[120px] transition-colors ${
                        snapshot.isDraggingOver
                          ? "bg-emerald-50/60 ring-2 ring-emerald-200"
                          : ""
                      }`}
                    >
                      {tasks.map((task, index) => (
                        <Draggable
                          key={task._id}
                          draggableId={task._id}
                          index={index}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`bg-white rounded-xl border p-3 transition-shadow ${
                                dragSnapshot.isDragging
                                  ? "shadow-lg ring-2 ring-[#198754] border-transparent"
                                  : "border-slate-200 shadow-sm hover:shadow-md cursor-grab"
                              } ${
                                task.status === "done"
                                  ? "opacity-60"
                                  : ""
                              }`}
                            >
                              {/* ── Card Header ─────────────────────────── */}
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="text-sm font-medium text-slate-800 line-clamp-2 flex-1 pr-1">
                                  {task.title}
                                </h4>
                                <div className="relative" ref={activeMenu === task._id ? menuRef : undefined}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMenu(
                                        activeMenu === task._id
                                          ? null
                                          : task._id
                                      );
                                    }}
                                    className="text-slate-400 hover:text-slate-600 p-0.5 rounded hover:bg-slate-100 transition-colors"
                                  >
                                    <MoreHorizontal className="h-3.5 w-3.5" />
                                  </button>

                                  {/* ── Dropdown Menu ───────────────────── */}
                                  <AnimatePresence>
                                    {activeMenu === task._id && (
                                      <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.1 }}
                                        className="absolute right-0 top-6 z-50 w-40 bg-white rounded-xl border border-slate-200 shadow-lg py-1 overflow-hidden"
                                      >
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkComplete(task._id);
                                          }}
                                          disabled={menuLoading === task._id}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                                        >
                                          {menuLoading === task._id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                          ) : (
                                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                          )}
                                          Mark Complete
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkBlocked(task._id);
                                          }}
                                          disabled={menuLoading === task._id}
                                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                                        >
                                          {menuLoading === task._id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                          ) : (
                                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                          )}
                                          Mark Blocked
                                        </button>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </div>

                              {/* ── Priority Badge ──────────────────────── */}
                              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                <span
                                  className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium ${
                                    PRIORITY_COLORS[task.priority] ||
                                    PRIORITY_COLORS.normal
                                  }`}
                                >
                                  {task.priority === "urgent" && (
                                    <AlertTriangle className="h-3 w-3 mr-0.5" />
                                  )}
                                  {formatLabel(task.priority)}
                                </span>

                                {/* Status badge for done/blocked */}
                                {task.status === "done" && (
                                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                    <CheckCircle2 className="h-3 w-3" />
                                    Done
                                  </span>
                                )}
                                {task.status === "blocked" && (
                                  <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                    <Lock className="h-3 w-3" />
                                    Blocked
                                  </span>
                                )}
                              </div>

                              {/* ── Due Date ────────────────────────────── */}
                              {task.dueAt && (
                                <div
                                  className={`flex items-center gap-1 text-xs mb-2 ${
                                    isOverdue(task.dueAt) && task.status !== "done"
                                      ? "text-red-600 font-medium"
                                      : "text-slate-500"
                                  }`}
                                >
                                  <Calendar className="h-3 w-3" />
                                  {new Date(task.dueAt).toLocaleDateString(
                                    "en-US",
                                    { month: "short", day: "numeric" }
                                  )}
                                  {isOverdue(task.dueAt) &&
                                    task.status !== "done" && (
                                      <span className="ml-1 text-red-500">
                                        Overdue
                                      </span>
                                    )}
                                </div>
                              )}

                              {/* ── Bottom Row: Assignees & Indicators ── */}
                              <div className="flex items-center justify-between mt-1">
                                {/* Assignee avatars */}
                                <div className="flex items-center -space-x-1.5">
                                  {task.ownerUserId &&
                                    typeof task.ownerUserId === "object" && (
                                      <div
                                        className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold ring-2 ring-white"
                                        style={{
                                          backgroundColor: "#198754",
                                        }}
                                        title={task.ownerUserId.name}
                                      >
                                        {getInitials(task.ownerUserId.name)}
                                      </div>
                                    )}
                                  {task.assigneeUserIds
                                    ?.slice(0, 3)
                                    .map((assignee) => (
                                      <div
                                        key={assignee._id}
                                        className="h-5 w-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold ring-2 ring-white bg-slate-500"
                                        title={assignee.name}
                                      >
                                        {getInitials(assignee.name)}
                                      </div>
                                    ))}
                                  {(task.assigneeUserIds?.length || 0) > 3 && (
                                    <div className="h-5 w-5 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 text-[9px] font-bold ring-2 ring-white">
                                      +{task.assigneeUserIds.length - 3}
                                    </div>
                                  )}
                                </div>

                                {/* Indicators */}
                                <div className="flex items-center gap-1.5">
                                  {task.requiresEvidence && (
                                    <span title="Requires evidence">
                                      <Paperclip className="h-3 w-3 text-slate-400" />
                                    </span>
                                  )}
                                  {task.dependencies &&
                                    task.dependencies.length > 0 && (
                                      <span title={`${task.dependencies.length} ${task.dependencies.length === 1 ? "dependency" : "dependencies"}`}>
                                        <Lock className="h-3 w-3 text-slate-400" />
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}

                      {provided.placeholder}

                      {/* ── Empty Column State ──────────────────────────── */}
                      {tasks.length === 0 && !snapshot.isDraggingOver && (
                        <div className="text-center py-8">
                          <p className="text-xs text-slate-400">No tasks</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
};

export default RealEstatePipelineTab;
